import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Download,
  Save,
  Copy,
  Loader2,
  Check,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Textarea } from '@/app/components/ui/textarea';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  useDocumentContent,
  useUpdateDocumentBody,
  usePublishRevision,
  useSaveAsNewDocument,
} from '@/hooks/use-setup-wizard';
import { DocumentVersionTimeline } from './document-version-timeline';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatTimeInTimeZone } from '@/lib/date-utils';
import type { GeneratedComplianceDocument } from '@/lib/types';

const AUTOSAVE_DELAY_MS = 1000;

interface DocumentSection {
  heading: string;
  content: string;
  signature_block?: { name: string; title: string };
}

export function GeneratedDocumentEditor({
  doc,
}: {
  doc: GeneratedComplianceDocument;
}) {
  const timeZone = useOrgTimeZone();
  const { data: content, isLoading: contentLoading } = useDocumentContent(doc.id);
  const updateBody = useUpdateDocumentBody();
  const publishRevision = usePublishRevision();
  const saveAsNew = useSaveAsNewDocument();

  const [sections, setSections] = useState<DocumentSection[]>([]);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showSaveAsModal, setShowSaveAsModal] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [publishError, setPublishError] = useState('');
  const [saveAsError, setSaveAsError] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bodyRef = useRef<Record<string, unknown> | null>(null);
  const isInitialLoadRef = useRef(true);
  const pendingSectionsRef = useRef<DocumentSection[] | null>(null);
  const docIdRef = useRef(doc.id);
  const mutateRef = useRef(updateBody.mutate);
  // eslint-disable-next-line react-hooks/refs
  mutateRef.current = updateBody.mutate;

  useEffect(() => {
    if (content?.body) {
      if (isInitialLoadRef.current) {
        const bodySections = content.body.sections as DocumentSection[] | undefined;
        setSections(bodySections || []);
        isInitialLoadRef.current = false;
      }
      bodyRef.current = content.body;
    }
  }, [content]);

  const debouncedSave = useCallback(
    (updatedSections: DocumentSection[]) => {
      pendingSectionsRef.current = updatedSections;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        pendingSectionsRef.current = null;
        const newBody = { ...bodyRef.current, sections: updatedSections };
        updateBody.mutate(
          { id: doc.id, body: newBody },
          {
            onSuccess: () => {
              setSavedAt(new Date().toISOString());
              setSaveError(null);
            },
            onError: () => {
              setSaveError('Failed to save — your changes may not be persisted.');
            },
          }
        );
      }, AUTOSAVE_DELAY_MS);
    },
    [doc.id, updateBody]
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        if (pendingSectionsRef.current) {
          const flushBody = { ...bodyRef.current, sections: pendingSectionsRef.current };
          mutateRef.current({ id: docIdRef.current, body: flushBody });
          pendingSectionsRef.current = null;
        }
      }
    };
  }, []);

  const handleSectionChange = (index: number, value: string) => {
    const updated = sections.map((s, i) =>
      i === index ? { ...s, content: value } : s
    );
    setSections(updated);
    debouncedSave(updated);
  };

  const flushPendingSave = (): Promise<void> => {
    if (timerRef.current && pendingSectionsRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      const flushBody = { ...bodyRef.current, sections: pendingSectionsRef.current };
      pendingSectionsRef.current = null;
      return new Promise((resolve, reject) => {
        updateBody.mutate(
          { id: doc.id, body: flushBody },
          {
            onSuccess: () => {
              setSavedAt(new Date().toISOString());
              setSaveError(null);
              resolve();
            },
            onError: () => {
              setSaveError('Failed to save \u2014 your changes may not be persisted.');
              reject(new Error('flush failed'));
            },
          }
        );
      });
    }
    return Promise.resolve();
  };

  const handlePublish = async () => {
    if (!changeSummary.trim()) {
      setPublishError('A change summary is required.');
      return;
    }
    setPublishError('');
    try {
      await flushPendingSave();
    } catch {
      return;
    }
    publishRevision.mutate(
      { id: doc.id, change_summary: changeSummary.trim() },
      {
        onSuccess: () => {
          setShowPublishModal(false);
          setChangeSummary('');
        },
      }
    );
  };

  const handleSaveAs = async () => {
    if (!newTitle.trim()) {
      setSaveAsError('A document title is required.');
      return;
    }
    setSaveAsError('');
    try {
      await flushPendingSave();
    } catch {
      return;
    }
    saveAsNew.mutate(
      { id: doc.id, title: newTitle.trim() },
      {
        onSuccess: () => {
          setShowSaveAsModal(false);
          setNewTitle('');
        },
      }
    );
  };

  const downloadUrl = `/api/v1/compliance/generated-documents/${doc.id}/download/`;
  const downloadLabel = doc.is_draft
    ? 'Download draft (not published)'
    : 'Download PDF';

  if (contentLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading document...
      </div>
    );
  }

  if (!content) {
    return (
      <p className="py-8 text-sm text-gray-500">
        Unable to load document content.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {/* Document header info */}
      <div className="rounded-lg bg-gray-50 p-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Company:</span>{' '}
            <span className="font-medium">{content.header.company_name}</span>
          </div>
          {content.header.trading_name && (
            <div>
              <span className="text-gray-500">Trading As:</span>{' '}
              <span className="font-medium">{content.header.trading_name}</span>
            </div>
          )}
          {content.header.irata_number && (
            <div>
              <span className="text-gray-500">IRATA #:</span>{' '}
              <span className="font-medium">{content.header.irata_number}</span>
            </div>
          )}
          <div>
            <span className="text-gray-500">Document #:</span>{' '}
            <span className="font-medium">{content.header.document_number}</span>
          </div>
          <div>
            <span className="text-gray-500">Version:</span>{' '}
            <span className="font-medium">{content.header.version}</span>
          </div>
          <div>
            <span className="text-gray-500">Issued:</span>{' '}
            <span className="font-medium">
              {formatDateOnly(content.header.issued_date)}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Next Review:</span>{' '}
            <span className="font-medium">
              {formatDateOnly(content.header.next_review_date)}
            </span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={doc.is_draft ? 'secondary' : 'default'}>
          {doc.is_draft ? 'Draft' : 'Published'}
        </Badge>
        <span className="text-xs text-gray-400">v{doc.version}</span>

        {savedAt && (
          <span className="flex items-center gap-1 text-xs text-green-600">
            <Check className="h-3 w-3" />
            Saved at {savedAt ? formatTimeInTimeZone(savedAt, timeZone) : ''}
          </span>
        )}
        {updateBody.isPending && (
          <span className="flex items-center gap-1 text-xs text-gray-400">
            <Loader2 className="h-3 w-3 animate-spin" />
            Saving...
          </span>
        )}
        {saveError && (
          <span className="flex items-center gap-1 text-xs text-red-600">
            <AlertCircle className="h-3 w-3" />
            {saveError}
          </span>
        )}

        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSaveAsModal(true)}
          >
            <Copy className="mr-1 h-4 w-4" />
            Save as new document
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPublishModal(true)}
          >
            <Save className="mr-1 h-4 w-4" />
            Publish revision
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
              <Download className="mr-1 h-4 w-4" />
              {downloadLabel}
            </a>
          </Button>
        </div>
      </div>

      {/* Editable sections */}
      <div className="space-y-4">
        {sections.map((section, idx) => (
          <div key={idx} className="space-y-1">
            <label className="text-sm font-semibold text-gray-800">
              {section.heading}
            </label>
            <Textarea
              value={section.content}
              onChange={(e) => handleSectionChange(idx, e.target.value)}
              className="min-h-[120px]"
            />
            {section.signature_block && (
              <div className="mt-2 rounded border border-dashed border-gray-300 p-3">
                <p className="text-xs text-gray-400">Signature Block</p>
                <p className="text-sm font-medium">
                  {section.signature_block.name}
                </p>
                <p className="text-xs text-gray-500">
                  {section.signature_block.title}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Version timeline */}
      <div className="border-t pt-4">
        <DocumentVersionTimeline docId={doc.id} />
      </div>

      {/* Publish modal */}
      <Dialog open={showPublishModal} onOpenChange={setShowPublishModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish Revision</DialogTitle>
            <DialogDescription>
              Create a new published version of this document. This will
              snapshot the current content as an immutable revision.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="change-summary">Change Summary *</Label>
            <Textarea
              id="change-summary"
              placeholder="Describe what changed in this revision..."
              value={changeSummary}
              onChange={(e) => {
                setChangeSummary(e.target.value);
                if (publishError) setPublishError('');
              }}
            />
            {publishError && (
              <p className="text-sm text-red-600">{publishError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPublishModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePublish}
              disabled={publishRevision.isPending || updateBody.isPending}
            >
              {publishRevision.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save as modal */}
      <Dialog open={showSaveAsModal} onOpenChange={setShowSaveAsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save as New Document</DialogTitle>
            <DialogDescription>
              Create a new document based on the current content. The new
              document will start at version 1.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="new-title">Document Title *</Label>
            <Input
              id="new-title"
              placeholder="Enter a title for the new document..."
              value={newTitle}
              onChange={(e) => {
                setNewTitle(e.target.value);
                if (saveAsError) setSaveAsError('');
              }}
              maxLength={50}
            />
            {saveAsError && (
              <p className="text-sm text-red-600">{saveAsError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSaveAsModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveAs} disabled={saveAsNew.isPending || updateBody.isPending}>
              {saveAsNew.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save as New
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
